import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/** Options passed to Checkout. `handler` / `modal.ondismiss` / `onFailed` are auto zone-wrapped. */
export interface RazorpayOptions {
  key: string;
  order_id: string;
  amount: number;
  currency: string;
  name?: string;
  description?: string;
  prefill?: { name?: string | null; email?: string | null; contact?: string | null };
  theme?: { color?: string };
  handler?: (resp: RazorpaySuccess) => void;
  modal?: { ondismiss?: () => void };
  onFailed?: (err: any) => void;
}

export interface RazorpaySuccess {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

/**
 * Thin wrapper around Razorpay Checkout.
 *
 * Lazily injects `checkout.js` the first time it's needed (browser-only, so it
 * never runs during SSR / prerender and isn't loaded on pages that don't pay).
 * Razorpay fires its handler/dismiss callbacks OUTSIDE Angular's zone, so every
 * callback is wrapped in NgZone.run() — otherwise the success UI wouldn't update.
 */
@Injectable({ providedIn: 'root' })
export class RazorpayService {
  private platformId = inject(PLATFORM_ID);
  private zone = inject(NgZone);

  private readonly SRC = 'https://checkout.razorpay.com/v1/checkout.js';
  private loading?: Promise<boolean>;

  /** Ensure the Checkout script is present. Resolves false on SSR or load failure. */
  load(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) return Promise.resolve(false);
    if ((window as any).Razorpay) return Promise.resolve(true);
    if (this.loading) return this.loading;

    this.loading = new Promise<boolean>((resolve) => {
      const s = document.createElement('script');
      s.src = this.SRC;
      s.async = true;
      s.onload = () => resolve(true);
      s.onerror = () => {
        this.loading = undefined; // allow a retry on the next attempt
        resolve(false);
      };
      document.head.appendChild(s);
    });
    return this.loading;
  }

  /** Open Checkout with the given options. Throws if the script hasn't loaded. */
  open(options: RazorpayOptions): any {
    const RZP = (window as any).Razorpay;
    if (!RZP) throw new Error('Razorpay Checkout is not available.');

    const wrap = <T extends (...a: any[]) => void>(fn?: T) =>
      fn ? (...args: any[]) => this.zone.run(() => fn(...args)) : undefined;

    const { onFailed, handler, modal, ...rest } = options;
    const rzp = new RZP({
      ...rest,
      handler: wrap(handler),
      modal: { ...(modal ?? {}), ondismiss: wrap(modal?.ondismiss) },
    });
    if (onFailed) rzp.on('payment.failed', wrap(onFailed));
    rzp.open();
    return rzp;
  }
}
